import * as marietta from '../..';
import { DIMENSION_CORRECTION_FREE, DIMENSION_CORRECTION_MODULE } from '../Constants';

/**
 * モジュール（寸法モード・ピッチ）に関する状態と方針を扱うクラス。
 * 建物種別(buildingType)とは独立した「寸法モード」の軸を担う。
 * ※ピッチ値そのものは Plan2DManager.modulePitch が保持する（既存の参照互換のため）。
 *   本クラスは寸法モードの状態と、ピッチの検証・正規化などの方針を集約する。
 */
export default class ModuleManager {
  // 寸法補正コード（DIMENSION_CORRECTION_FREE=0: フリー寸法 / DIMENSION_CORRECTION_MODULE=1: モジュール補正）。
  // 入出力データ（撮影アプリJSON・保存データ）と同じ数値表現で内部管理する。既定はフリー寸法。
  private _dimensionCorrection: number = DIMENSION_CORRECTION_FREE;

  public get dimensionCorrection(): number {
    return this._dimensionCorrection;
  }

  public set dimensionCorrection(value: number) {
    this._dimensionCorrection = value;
  }

  /** フリー寸法（実測基準）モードか */
  public isFree(): boolean {
    return this._dimensionCorrection === DIMENSION_CORRECTION_FREE;
  }

  /**
   * 入出力データ由来の寸法補正コードを適用する。
   * 0/1 以外（欠落・不正）は無視し、既存値（既定のフリー寸法）を維持する（後方互換）。
   * @param value 適用する寸法補正コード（0: フリー寸法 / 1: モジュール補正）
   */
  public applyDimensionCorrection(value: any): void {
    if (value === DIMENSION_CORRECTION_FREE || value === DIMENSION_CORRECTION_MODULE) {
      this._dimensionCorrection = value;
    }
  }

  // 撮影アプリ(LiDAR)から受け取ったモジュールピッチ(mm)。「モジュール補正」を選んだときの初期値に使う。
  // 既定はフォールバック値。LiDAR取込時・保存データ読込時に設定される。
  private _modulePitchFromApp: number = marietta.webcad.WebCADGlobal.MODULE_PITCH_DEFAULT;

  public get modulePitchFromApp(): number {
    return this._modulePitchFromApp;
  }

  public set modulePitchFromApp(value: number) {
    this._modulePitchFromApp = value;
  }

  /**
   * モジュールピッチが有効値か（有限数かつ下限〜上限の範囲内）。
   * 換算(convertMMToPix 等)のゼロ除算/NaN伝播を防ぐため、範囲外・非数は弾く。
   * @param value 判定するピッチ(mm)
   */
  public isValidPitch(value: number): boolean {
    return Number.isFinite(value)
      && value >= marietta.webcad.WebCADGlobal.MODULE_PITCH_MIN
      && value <= marietta.webcad.WebCADGlobal.MODULE_PITCH_MAX;
  }

  /**
   * モジュールピッチを正規化する。
   * 不正値(NaN/範囲外)なら現状値を維持し、有効値は整数mmへ丸める。
   * 換算(convertMMToPix 等)のゼロ除算・NaN伝播を入口で防ぐ。
   * @param value 設定しようとするピッチ(mm)
   */
  public normalizePitch(value: number): number {
    if (!this.isValidPitch(value)) {
      return marietta.webcad.WebCADGlobal.webcad2Dmanager.modulePitch; // 現状値を維持
    }
    return Math.round(value); // 保存(parseInt)に合わせ整数化
  }
}
